import React, { FC, useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

import Card from 'components/Card';
import { CardV2, useCardV2Enabled } from 'components/CardV2';
import SliderInteractive from 'components/SliderInteractive';

import { COLORS } from 'styles/constants';
import { CardSchema } from 'constants/graphqlTypes';
import { StyledCardWrap, StyledContainer } from './styled';

/**
 * Renders a single card, using CardV2 when supported or falling back to legacy Card.
 */
const PickSliderCard: FC<{ card: CardSchema; isDark: boolean }> = ({ card, isDark }) => {
  const useNewCard = useCardV2Enabled(card);

  if (useNewCard) {
    return <CardV2 card={card} variant="list" headerVariant="compact" showFooter disableAnimation />;
  }

  return (
    <Card
      card={card}
      isDark={isDark}
      isSpotlight
      showActivity
      hideFooterAvatars
      disableAnimation
      hideActionsAvatar
      hideActionsUserInfo
      hideTopAvatars
      isSmall
    />
  );
};

const PickSlider: FC<{
  items: CardSchema[];
  isDark: boolean;
  isInfinite?: boolean;
  children?: JSX.Element;
  onWaypointEnter?: () => void;
}> = ({ items, isDark, isInfinite = true, children, onWaypointEnter }) => {
  const [slideIndex, setSlideInd] = useState(0);

  useEffect(() => {
    if (items.length && slideIndex === items.length - (children ? 2 : 3) && !isInfinite) {
      onWaypointEnter?.();
    }
  }, [slideIndex]);

  return items.length === 1 && items?.[0] ? (
    <StyledCardWrap>
      <PickSliderCard card={items[0]} isDark={isDark} />
    </StyledCardWrap>
  ) : (
    <StyledContainer>
      <SliderInteractive
        onSlideChange={(ind) => setSlideInd(ind)}
        customSlidesPerView={2.25}
        spaceBetween={13}
        fontColor={isDark ? COLORS.white[100] : COLORS.brown.dark[100]}
        showBtnsSm={!!items.length}
        offset={12}
        isInfinite={isInfinite}
        isPrevDisabled={!isInfinite && slideIndex === 0}
        isNextDisabled={!isInfinite && slideIndex === items.length - (children ? 1 : 2)}
      >
        {items.map((item) => (
          <SwiperSlide key={item.id}>
            <PickSliderCard card={item} isDark={isDark} />
          </SwiperSlide>
        ))}
        {children && <SwiperSlide>{children}</SwiperSlide>}
      </SliderInteractive>
    </StyledContainer>
  );
};

export default PickSlider;
